import i18n from "@/i18n";
import SocketController from "@/services/Socket/socket.controller";
import {
  AdminUserType,
  dbAdminUserSchema,
  dbUserSchema,
  UserAdminUpdateProfileDetails,
  userAdminUpdateProfileDetailsSchema,
  UserType,
} from "@/types";
import { convertUserDetailsToDBExpectedFormat } from "@/utils/dataConversion";
import {
  validateArrayWithSchema,
  validateWithSchema,
} from "@/utils/dataValidators.zod";

// #region fetchUsersList
export async function fetchUsersList(): Promise<
  | {
      rStatus: "success";
      rData: UserType[]; // Replace with actual user data type
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "getUsersList",
      {},
      true
    );

    if (response.status === 200) {
      const validatedData = validateArrayWithSchema<UserType>(
        response.data,
        dbUserSchema,
        "User Type"
      );

      if (validatedData) {
        return {
          rStatus: "success",
          rData: validatedData,
        };
      } else {
        console.error("Invalid user data received", response.data);
        return {
          rStatus: "error",
          rData: i18n.t("Invalid user data received"),
        };
      }
    }

    if (response.status === 204) {
      return {
        rStatus: "error",
        rData: i18n.t("No users found"),
      };
    }

    if (response.status === 500) {
      return {
        rStatus: "error",
        rData: i18n.t("Internal server error. Please try again later"),
      };
    }

    if (response.status === 400) {
      return {
        rStatus: "error",
        rData: i18n.t("Bad request. Please contact support"),
      };
    }

    if (response.status === 403) {
      return {
        rStatus: "error",
        rData: i18n.t(
          "Access denied. You do not have permission to view users"
        ),
      };
    }

    if (response.status === 401) {
      return {
        rStatus: "error",
        rData: i18n.t("Unauthorized access. Please log in again"),
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch users list"),
    };
  } catch (error) {
    console.error("Error fetching users list:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch users list"),
    };
  }
}
// #endregion fetchUsersList

// #region updateUserRole
export async function updateUserRole(
  userID: string,
  roleAction: "approve" | "block" | "admin",
  note?: string
): Promise<{ rStatus: "success" } | { rStatus: "error"; rData: string }> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "roleUpdate",
      { userID, roleAction, reason: note },
      true
    );

    if (response.status === 200 || response.status === 202) {
      return { rStatus: "success" };
    }

    if (response.status === 400) {
      return {
        rStatus: "error",
        rData: i18n.t("Invalid user ID or role"),
      };
    }

    if (response.status === 404) {
      return {
        rStatus: "error",
        rData: i18n.t("User not found"),
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to update user role"),
    };
  } catch (error) {
    console.error("Error updating user role:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to update user role"),
    };
  }
}
// #endregion updateUserRole

// #region fetchAdminUsersList
export async function fetchAdminUsersList(): Promise<
  | {
      rStatus: "success";
      rData: AdminUserType[]; // Replace with actual user data type
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "getAdminList",
      {},
      true
    );

    if (response.status === 200) {
      const validatedData = validateArrayWithSchema<AdminUserType>(
        response.data,
        dbAdminUserSchema,
        "User Type"
      );

      if (validatedData) {
        return {
          rStatus: "success",
          rData: validatedData,
        };
      } else {
        console.error("Invalid user data received", response.data);
        return {
          rStatus: "error",
          rData: i18n.t("Invalid user data received"),
        };
      }
    }

    if (response.status === 204) {
      return {
        rStatus: "error",
        rData: i18n.t("No users found"),
      };
    }

    if (response.status === 500) {
      return {
        rStatus: "error",
        rData: i18n.t("Internal server error. Please try again later"),
      };
    }

    if (response.status === 400) {
      return {
        rStatus: "error",
        rData: i18n.t("Bad request. Please contact support"),
      };
    }

    if (response.status === 403) {
      return {
        rStatus: "error",
        rData: i18n.t(
          "Access denied. You do not have permission to view users"
        ),
      };
    }

    if (response.status === 401) {
      return {
        rStatus: "error",
        rData: i18n.t("Unauthorized access. Please log in again"),
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch users list"),
    };
  } catch (error) {
    console.error("Error fetching users list:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch users list"),
    };
  }
}
// #endregion fetchAdminUsersList

// #region fetchUserDetails
export async function fetchUserDetails(userID: string): Promise<
  | {
      rStatus: "success";
      rData: UserAdminUpdateProfileDetails;
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "getUserDetails",
      {
        userID: userID,
      },
      true
    );

    if (response.status === 200) {
      const validatedData = validateWithSchema<UserAdminUpdateProfileDetails>(
        response.data,
        userAdminUpdateProfileDetailsSchema,
        "User Profile"
      );

      if (validatedData) {
        return {
          rStatus: "success",
          rData: validatedData,
        };
      } else {
        console.error("Invalid user profile data received", response.data);
        return {
          rStatus: "error",
          rData: i18n.t("Invalid user profile data received"),
        };
      }
    }

    if (response.status === 204) {
      return {
        rStatus: "error",
        rData: i18n.t("No user profile found"),
      };
    }

    if (response.status === 400) {
      return {
        rStatus: "error",
        rData: i18n.t("Bad request. Please contact support."),
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch user profile"),
    };
  } catch (error) {
    console.error("Error fetching user profile:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch user profile"),
    };
  }
}
// #endregion fetchUserDetails

export async function sendUpdateUserDetails(
  userDetails: UserAdminUpdateProfileDetails
): Promise<{ rStatus: "success" } | { rStatus: "error"; rData: string }> {
  try {
    const transformedUserDetails =
      await convertUserDetailsToDBExpectedFormat(userDetails);

    const response = await SocketController.prototype.sendCommand(
      "updateUserDetails",

      transformedUserDetails,

      true
    );

    if (response.status === 200 || response.status === 202) {
      return { rStatus: "success" };
    }

    console.error("Error response:", response);

    if (response.status === 400) {
      return {
        rStatus: "error",
        rData: i18n.t("Bad request. Please contact support."),
      };
    }

    if (response.status === 204) {
      return {
        rStatus: "error",
        rData: i18n.t("User not found"),
      };
    }

    if (response.status === 403 || response.status === 401) {
      return {
        rStatus: "error",
        rData: i18n.t(
          "Access denied. You do not have permission to update this user"
        ),
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to update user details"),
    };
  } catch (error) {
    console.error("Error updating user details:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to update user details"),
    };
  }
}
